<template>
  <section>
    <b-card-actions
      action-collapse
      :show-loading="fetching || saving"
      title="Parâmetros do Sistema"
    >
      <!-- Campo de Pesquisa -->
      <b-row class="mb-1">
        <b-col md="12">
          <FormulateInput
            v-model="searchTerm"
            type="text"
            name="search"
            :placeholder="$t('Pesquisar por Nome, Chave ou Descrição...')"
            @input="onSearchChange"
          />
        </b-col>
      </b-row>

      <!-- Tabs dos Grupos -->
      <b-tabs
        v-if="groupedParameters.groups.length > 0"
        :active-tab-index="getTabIndex()"
        @input="onTabChange"
        nav-wrapper-class="mb-3"
      >
        <b-tab
          v-for="(group) in groupedParameters.groups"
          :key="group"
        >
          <template #title>
            <span class="mr-2">{{ group }}</span>
            <b-badge v-if="searchTerm" variant="primary">{{ getGroupCount(group) }}</b-badge>
          </template>

          <!-- Conteúdo dos Parâmetros -->
          <div v-if="filteredParameters.length > 0" class="mt-3">
            <parameter-list
              :parameters="filteredParameters"
              :fetching="fetching || saving"
              @edit="editParameter"
            />
          </div>
          <div v-else class="text-center text-muted py-5">
            {{ searchTerm ? $t('Nenhum parâmetro encontrado com o critério de pesquisa') : $t('Nenhum parâmetro disponível') }}
          </div>
        </b-tab>
      </b-tabs>

      <div v-else class="text-center text-muted py-5">
        {{ $t('Nenhum parâmetro disponível') }}
      </div>
    </b-card-actions>

    <!-- Modal de Edição -->
    <parameter-edit-modal
      ref="editModal"
      @saved="loadParameters"
    />
  </section>
</template>

<script>
import { BRow, BCol, BTabs, BTab, BBadge } from 'bootstrap-vue'
import BCardActions from '@/@core/components/b-card-actions/BCardActions.vue'
import ParameterList from './components/ParameterList.vue'
import ParameterEditModal from './components/ParameterEditModal.vue'

export default {
  name: 'ParameterMaintenance',
  components: {
    BRow,
    BCol,
    BTabs,
    BTab,
    BBadge,
    BCardActions,
    ParameterList,
    ParameterEditModal,
  },
  data() {
    return {
      fetching: false,
      saving: false,
      parameters: [],
      activeGroup: null,
      searchTerm: '',
    }
  },
  computed: {
    groupedParameters() {
      // Obter lista filtrada de parâmetros baseado na pesquisa
      const filtered = this.getSearchResults()
      
      // Extrair grupos únicos dos parâmetros filtrados
      const groups = new Set()
      filtered.forEach(param => {
        groups.add(param.group || 'Outros')
      })
      
      return {
        groups: Array.from(groups).sort(),
      }
    },
    filteredParameters() {
      const searchFiltered = this.getSearchResults()
      return searchFiltered.filter(param => (param.group || 'Outros') === this.activeGroup)
    },
  },
  async mounted() {
    await this.loadParameters()
  },
  methods: {
    async loadParameters() {
      try {
        this.fetching = true
        const response = await this.$http.get('/api/parameters')
        this.parameters = response.data || []
        
        // Inicializar com primeiro group
        if (!this.activeGroup && this.groupedParameters.groups.length > 0) {
          const [firstGroup] = this.groupedParameters.groups
          this.activeGroup = firstGroup
        }
      } catch (error) {
        this.showError({ error, message: this.$t('Erro ao carregar parâmetros') })
      } finally {
        this.fetching = false
      }
    },
    editParameter(parameter) {
      this.$refs.editModal.open(parameter)
    },
    onTabChange(index) {
      if (this.groupedParameters.groups[index]) {
        this.activeGroup = this.groupedParameters.groups[index]
      }
    },
    getSearchResults() {
      if (!this.searchTerm.trim()) {
        return this.parameters
      }

      const term = this.searchTerm.toLowerCase().trim()
      return this.parameters.filter(param => {
        const name = (param.name || '').toLowerCase()
        const key = (param.key || '').toLowerCase()
        const value = (param.value || '').toLowerCase()
        const description = (param.description || '').toLowerCase()

        return name.includes(term) || key.includes(term) || value.includes(term) || description.includes(term)
      })
    },
    getGroupCount(group) {
      const filtered = this.getSearchResults()
      return filtered.filter(param => (param.group || 'Outros') === group).length
    },
    onSearchChange() {
      // Quando a pesquisa muda, reselecionar o grupo
      if (this.groupedParameters.groups.length > 0 && !this.groupedParameters.groups.includes(this.activeGroup)) {
        const [firstGroup] = this.groupedParameters.groups
        this.activeGroup = firstGroup
      }
    },
    clearSearch() {
      this.searchTerm = ''
      this.onSearchChange()
    },
    getTabIndex() {
      const index = this.groupedParameters.groups.indexOf(this.activeGroup)
      return index >= 0 ? index : 0
    },
  },
}
</script>

<style scoped>
/* Formulate styling */
::v-deep .formulate-input {
  margin-bottom: 0;
}

::v-deep .formulate-input-wrapper--text input {
  border: 1px solid #d4d4de;
  border-radius: 0.375rem;
  padding: 0.625rem 0.875rem;
  font-size: 0.875rem;
  transition: border-color 0.3s ease;
}

::v-deep .formulate-input-wrapper--text input:focus {
  border-color: #7367f0;
  box-shadow: 0 0 0 0.2rem rgba(115, 103, 240, 0.25);
}

/* Tab styling */
::v-deep .nav-tabs {
  border-bottom: 2px solid #f5f5fa;
}

::v-deep .nav-tabs .nav-link {
  color: #6e6b7b;
  border: none;
  border-bottom: 2px solid transparent;
  font-weight: 500;
  padding: 0.75rem 1.25rem;
  transition: all 0.3s ease;
}

::v-deep .nav-tabs .nav-link:hover {
  color: #7367f0;
  border-bottom-color: rgba(115, 103, 240, 0.5);
}

::v-deep .nav-tabs .nav-link.active {
  color: #7367f0;
  background-color: transparent;
  border-bottom-color: #7367f0;
}
</style>
